import React, { useState } from 'react';

import Textarea from 'component/Layout/Form/Textarea';

import { Provider } from 'jotai';

import { useSetSearchAtom } from 'hooks/form/useSetSearchAtom';
import { QaIdentifier, useQa } from 'hooks/useQa';
import { useTranslation } from 'hooks/useTranslation';

import { filterAtom } from 'data/Atom/Filter';
import { searchStore } from 'data/Atom/Store';

import { ActiveFilter } from 'proto/Search/ActiveFilter';

import { useAiSmartFilterData } from './__hooks/useAiSmartFilterData';
import { useApplyFilter } from './__hooks/useApplyFilter';
import ApplyFilterButton from './__partial/ApplyFilterButton';
import ErrorMessage from './__partial/ErrorMessage';
import FilterChips from './__partial/FilterChips';
import Header from './__partial/Header';
import { SearchState } from './types';

import styles from './AiSmartFilter.module.scss';

const AiSmartFilter: React.FC = () => {
    const { t } = useTranslation();
    const [inputValue, setInputValue] = useState('');
    const [filters, setFilters] = useState<ActiveFilter[]>([]);
    const updateFilter = useSetSearchAtom(filterAtom);
    const filterData = useAiSmartFilterData();
    const currentUrl = window.location.href;
    const KIFilter = 'KIFilter';
    const qaAttr = useQa(QaIdentifier.Ai_Smart_Filter);
    const qaAttrInput = useQa(QaIdentifier.Ai_Smart_Filter_Input);
    const {
        handleApplyFilter,
        handleCancelRequest,
        searchState,
        errorMessage,
        filters: fetchedFilters,
    } = useApplyFilter(inputValue, currentUrl, filterData, updateFilter);

    React.useEffect(() => {
        setFilters(fetchedFilters);
    }, [fetchedFilters]);

    const onKeyUp = (e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === 'Enter') {
            e.currentTarget.blur();
            handleApplyFilter();
        }
    };

    return (
        <div className={styles.AiSmartFilter} {...qaAttr}>
            <Header />
            {errorMessage && (
                <ErrorMessage
                    title={errorMessage.title}
                    hint={errorMessage.hint}
                />
            )}
            <p>{t('TRANS-what-are-you-searching-for')}</p>
            <Provider store={searchStore}>
                <FilterChips filters={filters} />
            </Provider>
            <div className={styles.InputSection}>
                <div className={styles.TextareaWrapper} {...qaAttrInput}>
                    <Textarea
                        textarea={{
                            name: KIFilter,
                            id: KIFilter,
                            placeholder: t(
                                'TRANS-example-looking-for-good-rated-hotel-with-pool-and-free-cancellation'
                            ),
                            value: inputValue,
                            maxLength: 500,
                            rows: 4,
                        }}
                        isDisabled={searchState !== SearchState.Inactive}
                        customClass={{
                            TextareaWrapper: styles.TextareaWrapper,
                        }}
                        onChange={(value) => setInputValue(value)}
                        onClear={() => setInputValue('')}
                        onKeyUp={onKeyUp}
                    />
                </div>
                <ApplyFilterButton
                    searchState={searchState}
                    onApplyFilter={handleApplyFilter}
                    onCancelRequest={handleCancelRequest}
                />
            </div>
            <hr className={styles.Divider} />
        </div>
    );
};

export default AiSmartFilter;
